import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { IconPencil, IconPlus, IconTool, IconTrash } from "@tabler/icons-react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import PageContainer from "../layouts/PageContainer";
import {
  Card,
  EmptyState,
  ErrorBanner,
  LoadingPanel,
  PageHeader,
  Pill,
  RefreshButton,
} from "../ui";
import { axiosInstance } from "../../utils/axiosInstance";
import { Location } from "../../interfaces/interfaces";
import { formatAgo } from "../cleaners/cleanerAttendanceUtils";
import EngineerVisitModal, { EngineerVisitStore } from "./EngineerVisitModal";
import { EngineerVisit } from "./engineerVisitTypes";
import {
  deleteVisit,
  engineerVisitsErrorMessage,
  useEngineerVisits,
} from "./useEngineerVisits";
import { formatVisitDate, taskLabel } from "./engineerVisitUtils";

export default function EngineerVisitsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const locationFilter = Number(searchParams.get("locationId")) || null;
  const { data, loading, error, refreshing, reload } = useEngineerVisits({
    locationId: locationFilter,
  });
  const [stores, setStores] = useState<EngineerVisitStore[]>([]);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<EngineerVisit | null>(null);
  const [deleting, setDeleting] = useState<EngineerVisit | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .get<Location[]>("/location")
      .then((res) => {
        if (cancelled) return;
        setStores(
          res.data
            .map((l) => ({ locationId: l.locationId, name: l.name }))
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      })
      .catch(() => {
        if (!cancelled) setStores([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const taskOptions = data?.taskOptions ?? [];

  const visibleVisits = useMemo(() => {
    const visits = data?.visits ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return visits;
    return visits.filter(
      (v) =>
        v.engineerName.toLowerCase().includes(q) ||
        (v.notes ?? "").toLowerCase().includes(q) ||
        (v.locationName ?? "").toLowerCase().includes(q)
    );
  }, [data, search]);

  const setLocationFilter = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set("locationId", value);
    else next.delete("locationId");
    setSearchParams(next, { replace: true });
  };

  const openNew = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (visit: EngineerVisit) => {
    setEditing(visit);
    setModalOpen(true);
  };

  const handleSaved = () => {
    setModalOpen(false);
    setEditing(null);
    reload({ soft: true });
  };

  const openDelete = (visit: EngineerVisit) => {
    setDeleteError(null);
    setDeleting(visit);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await deleteVisit(deleting.id);
      setDeleting(null);
      reload({ soft: true });
    } catch (err: any) {
      setDeleteError(engineerVisitsErrorMessage(err, "Failed to delete the visit."));
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Engineer visits"
        subtitle={`What engineers did on each store visit${refreshing ? " · refreshing…" : ""}`}
        actions={
          <>
            <RefreshButton onClick={() => reload({ soft: true })} loading={loading || refreshing} />
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={openNew}
              disabled={!data}
            >
              <IconPlus size={16} />
              Log visit
            </button>
          </>
        }
      />

      {error ? <ErrorBanner onRetry={() => reload()}>{error}</ErrorBanner> : null}

      <Card flush>
        <div className="sq-toolbar">
          <label className="sq-field">
            <span>Store</span>
            <select
              className="sq-input"
              value={locationFilter ?? ""}
              onChange={(e) => setLocationFilter(e.target.value)}
            >
              <option value="">All stores</option>
              {stores.map((s) => (
                <option key={s.locationId} value={s.locationId}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <label className="sq-field is-grow">
            <span>Search</span>
            <input
              type="search"
              className="sq-input"
              placeholder="Engineer, store or notes"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
        </div>

        {loading && !data ? <LoadingPanel label="Loading engineer visits" rows={6} /> : null}

        {data && visibleVisits.length === 0 ? (
          <EmptyState
            icon={<IconTool size={24} />}
            title={
              data.visits.length === 0
                ? "No engineer visits logged yet"
                : "No visits match the current filters"
            }
          />
        ) : null}

        {data && visibleVisits.length > 0 ? (
          <div className="sq-table-wrap">
            <table className="sq-table is-stacked">
              <thead>
                <tr>
                  <th scope="col">Visited</th>
                  <th scope="col">Store</th>
                  <th scope="col">Engineer</th>
                  <th scope="col">What they did</th>
                  <th scope="col">Notes</th>
                  <th scope="col" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {visibleVisits.map((v) => (
                  <tr key={v.id}>
                    <td data-label="Visited">
                      <div className="sq-strong">{formatVisitDate(v.visitedAt)}</div>
                      <div className="sq-muted">{formatAgo(v.visitedAt)}</div>
                    </td>
                    <td className="cell-title sq-strong">{v.locationName ?? "—"}</td>
                    <td data-label="Engineer">{v.engineerName}</td>
                    <td data-label="What they did" className="cell-wide">
                      {v.tasks.length === 0 ? (
                        <span className="sq-muted">—</span>
                      ) : (
                        <div className="sq-tasks">
                          {v.tasks.map((t) => (
                            <Pill key={t} tone="info">
                              {taskLabel(t, taskOptions)}
                            </Pill>
                          ))}
                        </div>
                      )}
                    </td>
                    <td
                      data-label="Notes"
                      className={`cell-wide${v.notes ? "" : " is-empty"}`}
                      style={{ whiteSpace: "pre-wrap" }}
                    >
                      {v.notes || "—"}
                    </td>
                    <td className="cell-actions">
                      <button
                        type="button"
                        className="sq-icon-btn"
                        title="Edit visit"
                        aria-label={`Edit visit to ${v.locationName ?? "store"}`}
                        onClick={() => openEdit(v)}
                      >
                        <IconPencil size={18} />
                      </button>
                      <button
                        type="button"
                        className="sq-icon-btn is-danger"
                        title="Delete visit"
                        aria-label={`Delete visit to ${v.locationName ?? "store"}`}
                        onClick={() => openDelete(v)}
                      >
                        <IconTrash size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Card>

      <EngineerVisitModal
        open={modalOpen}
        visit={editing}
        stores={stores}
        taskOptions={taskOptions}
        engineerNames={data?.engineerNames ?? []}
        defaultLocationId={locationFilter}
        onClose={() => setModalOpen(false)}
        onSaved={handleSaved}
      />

      <Dialog
        open={deleting != null}
        onClose={() => !deleteBusy && setDeleting(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete visit?</DialogTitle>
        <DialogContent>
          {deleteError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          ) : null}
          {deleting ? (
            <p className="mb-0">
              Delete {deleting.engineerName}'s visit to {deleting.locationName ?? "this store"} on{" "}
              {formatVisitDate(deleting.visitedAt)}? This can't be undone.
            </p>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)} disabled={deleteBusy}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={confirmDelete} disabled={deleteBusy}>
            {deleteBusy ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
}
